
import React, { useState, useEffect } from 'react';
import { SimulationConfig, MelodyInstrument } from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { AUDIO } from '@/services/AudioService';
import { CloudService } from '@/services/CloudService';
import { PRESET_LIBRARY } from '@/data/presets';
import { 
  Zap, 
  RotateCcw, 
  Scissors, 
  Upload, 
  Cloud, 
  Cpu, 
  Layers, 
  Waves
} from 'lucide-react';
import { toast } from 'sonner';

// Subcomponents
import { VisualSlicer } from './components/settings/audio/SimVisualSlicer';
import { PresetLibrary } from './components/settings/audio/SimPresetLibrary';
import { AudioOverrides } from './components/settings/audio/AudioOverrides';
import { ManualRack } from './components/settings/audio/ManualRack';

import { motion, AnimatePresence } from 'motion/react';

import { ControlGroup } from './ControlGroup';
import { InfoPopup } from './InfoPopup';

import { audioCache } from '@/lib/LocalPersistentStore';

interface AudioPostProcessingSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  cloudSaves?: any[];
}

export const AudioPostProcessingSection: React.FC<AudioPostProcessingSectionProps> = ({ config, setConfig, cloudSaves = [] }) => {
  const [rackTarget, setRackTarget] = useState<'MELODY' | 'FX'>('MELODY');
  const [isSongUploading, setIsSongUploading] = useState(false);
  const [cloudSongs, setCloudSongs] = useState<any[]>([]);

  const fetchAllSources = async () => {
    try {
      const [songs, converted] = await Promise.all([
        CloudService.getSongs(),
        CloudService.getConvertedSongs()
      ]);
      setCloudSongs([...songs, ...converted]);
    } catch (e) {
      console.warn("Could not fetch song sources", e);
    }
  };

  useEffect(() => {
    fetchAllSources();
  }, []);

  const smartRandomize = () => {
    const preset = PRESET_LIBRARY[Math.floor(Math.random() * PRESET_LIBRARY.length)];
    setConfig(p => ({
        ...p,
        ...preset.settings,
        audioReverbLevel: Math.max(0, Math.min(1, (preset.settings.audioReverbLevel || 0) + (Math.random() - 0.5) * 0.2)),
        audioDelayTime: Math.max(0.05, Math.min(1.0, (preset.settings.audioDelayTime || 0.3) + (Math.random() - 0.5) * 0.2)),
    }));
  };

  const hardReset = () => {
      setConfig(p => ({
          ...p,
          audioReverbLevel: 0,
          audioDistortionLevel: 0,
          audioDelayTime: 0.3,
          audioDelayFeedback: 0,
          audioEcho: false,
          fxReverbLevel: 0,
          fxDistortionLevel: 0,
          fxEcho: false,
          audioLowPassFreq: 20000,
          audioLowPassResonance: 1.0,
          melodyInstrument: 'GRAND_PIANO',
          ballCollisionSoundType: 'MELODY',
          audioPitchShift: 0,
          audioCompThreshold: -12,
          audioCompRatio: 4,
          universalForceInstrument: false
      }));
  };

  const loadSongSource = async (name: string, dataUrl: string) => {
      if (!dataUrl) return;
      setIsSongUploading(true);
      try {
          // Phase 6: Check local cache first
          let ab = await audioCache.get(dataUrl);
          
          if (!ab) {
            const res = await fetch(dataUrl);
            if (!res.ok) {
              if (res.status === 403) {
                try {
                  const errorData = await res.json();
                  if (errorData && errorData.error === "Asset Pending Security Scan") {
                    throw new Error("Asset is currently being scanned for security. Please try again in 30 seconds.");
                  }
                } catch (e) {
                  // Fallback
                }
                throw new Error("403 Forbidden - Access Denied or Asset Pending Scan");
              }
              throw new Error(`Fetch failed: ${res.status}`);
            }
            ab = await res.arrayBuffer();
            // Cache it persistently
            await audioCache.set(dataUrl, ab);
          }

          const duration = await AUDIO.uploadSongForSlicing(ab);
          
          if (duration > 0) {
              setConfig(p => ({
                  ...p,
                  songTotalDuration: duration,
                  ballCollisionSoundType: 'SONG_SLICE',
                  melodyVolume: 1.0,
                  audioEcho: false,
                  audioReverbLevel: 0.1,
                  // @ts-ignore
                  selectedSlicerSong: name
              }));
              toast.success(`Loaded Source: ${name}`);
          }
      } catch(e) {
          console.error("Failed to load song source", e);
          toast.error("Failed to load audio source");
      } finally {
          setIsSongUploading(false);
      }
  };

  const handleSongUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // OPTIMIZATION: Only support .mp3 and .wav
      if (!file.name.toLowerCase().endsWith('.mp3') && !file.name.toLowerCase().endsWith('.wav')) {
        toast.error("Invalid format. Please upload .mp3 or .wav");
        return;
      }

      setIsSongUploading(true);
      
      try {
        // 1. Upload to R2 Bucket
        const uploadResult = await CloudService.uploadFile(file);
        if (!uploadResult) {
          setIsSongUploading(false);
          return;
        }

        const name = file.name.replace(/\.[^/.]+$/, "");
        
        // 2. Register with Song Database (Reference URL)
        await CloudService.registerSong(name, uploadResult.url, undefined, uploadResult.size);
        fetchAllSources();

        // 3. Analyze for Slicer
        const duration = await AUDIO.uploadSongForSlicing(file);
        
        if (duration > 0) {
            setConfig(p => ({
                ...p,
                songTotalDuration: duration,
                ballCollisionSoundType: 'SONG_SLICE',
                melodyVolume: 1.0,
                audioEcho: false,
                audioReverbLevel: 0.1,
                // @ts-ignore
                selectedSlicerSong: name
            }));
            toast.success(`Song Analyzed: ${name}`);
        }
      } catch (e: any) {
        console.error("Upload/Analysis failed", e);
        toast.error(e.message || "Failed to process song");
      } finally {
        setIsSongUploading(false);
      }
  };

  const allAvailableSources = [
    ...cloudSongs.map(s => ({ id: s.id, name: s.name, data: s.data, type: 'CLOUD' })),
    ...config.userSounds.map(s => ({ id: s.id, name: s.name, data: s.url || s.base64, type: 'LOCAL' }))
  ].filter(s => s.data);

  const isM = rackTarget === 'MELODY';
  const instruments: MelodyInstrument[] = [
      'GRAND_PIANO', 'BRIGHT_PIANO', 'ELECTRIC_PIANO', 'HONKY_TONK', 'HARPSICHORD', 'CLAVINET',
      'CELESTA', 'GLOCKENSPIEL', 'MUSIC_BOX', 'VIBRAPHONE', 'MARIMBA', 'XYLOPHONE', 'TUBULAR_BELLS', 'DULCIMER',
      'DRAWBAR_ORGAN', 'PERCUSSIVE_ORGAN', 'ROCK_ORGAN', 'CHURCH_ORGAN', 'REED_ORGAN', 'ACCORDION', 'HARMONICA', 'TANGO_ACCORDION',
      'NYLON_GUITAR', 'STEEL_GUITAR', 'JAZZ_GUITAR', 'CLEAN_GUITAR', 'MUTED_GUITAR', 'OVERDRIVEN_GUITAR', 'DISTORTION_GUITAR', 'GUITAR_HARMONICS',
      'ACOUSTIC_BASS', 'FINGER_BASS', 'PICK_BASS', 'FRETLESS_BASS', 'SLAP_BASS_1', 'SLAP_BASS_2', 'SYNTH_BASS_1', 'SYNTH_BASS_2', 'SYNTH_BASS_3', 'SYNTH_BASS_4',
      'VIOLIN', 'VIOLA', 'CELLO', 'CONTRABASS', 'TREMOLO_STRINGS', 'PIZZICATO_STRINGS', 'ORCHESTRAL_HARP', 'TIMPANI',
      'STRING_ENSEMBLE_1', 'STRING_ENSEMBLE_2', 'SYNTH_STRINGS_1', 'SYNTH_STRINGS_2', 'CHOIR_AAHS', 'VOICE_OOHS', 'SYNTH_VOICE', 'ORCHESTRA_HIT', 'ORCHESTRA_HIT_3',
      'TRUMPET', 'TROMBONE', 'TUBA', 'MUTED_TRUMPET', 'FRENCH_HORN', 'BRASS_SECTION', 'SYNTH_BRASS_1', 'SYNTH_BRASS_2',
      'SOPRANO_SAX', 'ALTO_SAX', 'TENOR_SAX', 'BARITONE_SAX', 'OBOE', 'ENGLISH_HORN', 'BASSOON', 'CLARINET',
      'PICCOLO', 'FLUTE', 'RECORDER', 'PAN_FLUTE', 'BLOWN_BOTTLE', 'SHAKUHACHI', 'WHISTLE', 'OCARINA',
      'SQUARE_LEAD', 'SAWTOOTH_LEAD', 'CALLIOPE_LEAD', 'CHIFF_LEAD', 'CHARANG_LEAD', 'VOICE_LEAD', 'FIFTHS_LEAD', 'BASS_LEAD', '8BIT_LEAD', 'SYNTH_PLUCK', 'SYNTH_LEAD_3', 'SYNTH_LEAD_4',
      'NEW_AGE_PAD', 'WARM_PAD', 'POLYSYNTH_PAD', 'CHOIR_PAD', 'BOWED_PAD', 'METALLIC_PAD', 'HALO_PAD', 'SWEEP_PAD', 'SPACE_VOICE', 'ALIEN_DRONE',
      'RAIN_FX', 'SOUNDTRACK_FX', 'CRYSTAL_FX', 'ATMOSPHERE_FX', 'BRIGHTNESS_FX', 'GOBLINS_FX', 'ECHOES_FX', 'SCI_FI_FX', 'ETHEREAL_CHIME', 'THUNDER_FX', 'WIND_FX', 'WATER_FX', 'BIRD_TWEET', 'TELEPHONE_RING', 'HELICOPTER', 'APPLAUSE', 'GUNSHOT',
      'SITAR', 'BANJO', 'SHAMISEN', 'KOTO', 'KALIMBA', 'BAGPIPE', 'FIDDLE', 'SHANAI',
      'TINKLE_BELL', 'AGOGO', 'STEEL_DRUMS', 'WOODBLOCK', 'TAIKO_DRUM', 'MELODIC_TOM', 'SYNTH_DRUM', 'REVERSE_CYMBAL', 'PURE_SINE', 'CHURCH_BELL', 'HAND_BELL'
  ];

  return (
    <div className="flex flex-col gap-8">
      {/* Simulation Controls */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={smartRandomize} className={`h-14 rounded-2xl flex items-center justify-center gap-3 transition-all active:scale-95 border ${config.darkMode ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-600 shadow-sm'}`}>
          <Zap className="w-4 h-4 fill-current" />
          <span className="text-[10px] font-display font-black uppercase tracking-widest">Smart Rand</span>
        </button>
        <button onClick={hardReset} className={`h-14 rounded-2xl flex items-center justify-center gap-3 transition-all active:scale-95 border ${config.darkMode ? 'bg-zinc-800 border-white/5 text-white' : 'bg-white border-zinc-950/10 text-zinc-950 shadow-sm'}`}>
          <RotateCcw className="w-4 h-4" />
          <span className="text-[10px] font-display font-black uppercase tracking-widest">Global Reset</span>
        </button>
      </div>

      {/* Slicer Engine */}
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-neon-cyan/20 flex items-center justify-center shadow-lg border border-cyan-500/20">
              <Scissors className="w-4 h-4 text-cyan-500" />
            </div>
            <div className="flex items-center gap-2">
              <h3 className="text-[10px] font-display font-black uppercase tracking-[0.2em] opacity-40">Slicer Node</h3>
              <InfoPopup settingKey="slicerNode" darkMode={config.darkMode} />
            </div>
          </div>
          {config.ballCollisionSoundType === 'SONG_SLICE' && (
            <div className="px-3 py-1 rounded-full bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest animate-pulse shadow-lg shadow-emerald-500/20">Active</div>
          )}
        </div>

        <label className={`w-full py-10 rounded-[2rem] border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all mb-4 ${isSongUploading ? 'bg-zinc-950/10 border-zinc-950/20' : (config.darkMode ? 'border-white/10 hover:bg-white/5' : 'border-zinc-950/10 hover:bg-white bg-white/50 shadow-sm')}`}>
          <Upload className={`w-5 h-5 opacity-40 ${isSongUploading ? 'animate-bounce' : ''}`} />
          <span className="text-[9px] font-display font-black uppercase opacity-40 tracking-widest">{isSongUploading ? 'Analyzing Audio...' : 'Import New Waveform Source'}</span>
          <input type="file" accept="audio/*" onChange={handleSongUpload} className="hidden" />
        </label>

        {allAvailableSources.length > 0 && (
          <div className="flex flex-col gap-3 mb-8">
            <span className="text-[9px] font-display font-black uppercase tracking-[0.2em] opacity-30 ml-1 flex items-center gap-2">
              <Cloud className="w-3 h-3" />
              Source Library ({allAvailableSources.length})
            </span>
            <div className={`flex flex-col gap-1.5 max-h-48 overflow-y-auto p-2 rounded-2xl border ${config.darkMode ? 'bg-black/30 border-white/5 shadow-inner' : 'bg-white border-zinc-950/5 shadow-inner'}`}>
              {allAvailableSources.map(s => (
                <button
                  key={`${s.type}-${s.id}`}
                  onClick={() => loadSongSource(s.name, s.data)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all group ${
                    // @ts-ignore
                    config.selectedSlicerSong === s.name 
                    ? 'bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 shadow-lg' 
                    : (config.darkMode ? 'hover:bg-white/5 text-white/60' : 'hover:bg-zinc-50 text-zinc-900')
                  }`}
                >
                  <div className="flex items-center gap-3 truncate">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${// @ts-ignore
                    config.selectedSlicerSong === s.name ? 'bg-white/10' : 'bg-zinc-100 text-zinc-400'}`}>
                      {s.type === 'CLOUD' ? <Cloud className="w-3 h-3" /> : <Upload className="w-3 h-3" />}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest truncate">{s.name}</span>
                  </div>
                  {/* @ts-ignore */}
                  {config.selectedSlicerSong === s.name && (
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between px-1">
              <span className="text-[9px] font-display font-black uppercase tracking-[0.2em] opacity-30">Granular Timeline</span>
              <button 
                onClick={() => setConfig(p => ({...p, songSliceSequence: []}))}
                className="text-[9px] font-black uppercase text-red-500/40 hover:text-red-500 transition-colors"
              >
                Clear
              </button>
            </div>
            <div className={`p-2 rounded-[2rem] border ${config.darkMode ? 'bg-black/40 border-white/5 shadow-inner' : 'bg-white border-zinc-950/5 shadow-inner'}`}>
              <VisualSlicer config={config} setConfig={setConfig} />
            </div>
          </div>

          <div className="flex flex-col gap-4">
             <SmartInput 
                label="Slice Scale (S)" 
                settingKey="songSliceDuration"
                min={0.01} max={2.0} step={0.01} 
                value={config.songSliceDuration || 0.15} 
                onChange={v => setConfig(p => ({...p, songSliceDuration: isNaN(v) ? 0.15 : v}))} 
                darkMode={config.darkMode} 
                fullWidth
             />
             
             <div className="grid grid-cols-1 gap-2">
               <div className={`p-4 rounded-xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-950/5 shadow-sm'}`}>
                 <ControlGroup label="Continuous Processing" settingKey="songSliceContinuousMode" darkMode={config.darkMode}>
                    <Toggle active={config.songSliceContinuousMode} onClick={() => setConfig(p => ({...p, songSliceContinuousMode: !p.songSliceContinuousMode}))} />
                 </ControlGroup>
               </div>
               <div className={`p-4 rounded-xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-950/5 shadow-sm'}`}>
                 <ControlGroup label="Engine Sync Mute" settingKey="audioMutedInSlicer" darkMode={config.darkMode}>
                    <Toggle active={config.audioMutedInSlicer} onClick={() => setConfig(p => ({...p, audioMutedInSlicer: !p.audioMutedInSlicer}))} />
                 </ControlGroup>
               </div>
             </div>
          </div>
        </div>
      </div>

      {/* Logic Patterns */}
      <div className="flex flex-col gap-8">
        <div className="flex items-center gap-3 mb-8 px-1">
          <div className="w-8 h-8 rounded-xl bg-neon-cyan/20 flex items-center justify-center shadow-lg border border-cyan-500/20">
            <Cpu className="w-4 h-4 text-cyan-500" />
          </div>
          <div className="flex items-center gap-2">
            <h3 className="text-[10px] font-display font-black uppercase tracking-[0.2em] opacity-40">Frequency Matrix</h3>
            <InfoPopup settingKey="audioEffects" darkMode={config.darkMode} />
          </div>
        </div>

        <div className="flex flex-col gap-8">
           <AudioOverrides config={config} setConfig={setConfig} instruments={instruments} />

           <div className="flex flex-col gap-4">
              <div className={`grid grid-cols-2 gap-2 p-1.5 rounded-[1.5rem] border ${config.darkMode ? 'bg-black/30 border-white/5' : 'bg-white border-zinc-950/5 shadow-inner'}`}>
                  <button onClick={() => setRackTarget('MELODY')} className={`h-10 rounded-xl text-[9px] font-display font-black uppercase tracking-widest transition-all ${isM ? 'bg-neon-cyan text-white shadow-xl shadow-cyan-500/20' : 'text-zinc-400 hover:text-zinc-600'}`}>Melody Core</button>
                  <button onClick={() => setRackTarget('FX')} className={`h-10 rounded-xl text-[9px] font-display font-black uppercase tracking-widest transition-all ${!isM ? 'bg-neon-cyan text-white shadow-xl shadow-cyan-500/20' : 'text-zinc-400 hover:text-zinc-600'}`}>FX Relay</button>
              </div>
              
              <div className="flex flex-col gap-4">
                  <ManualRack config={config} setConfig={setConfig} isM={isM} instruments={instruments} />
              </div>
           </div>

           <div className="flex flex-col gap-4">
             <div className="flex items-center gap-2 px-1 opacity-30">
               <Waves className="w-3.5 h-3.5" />
               <span className="text-[9px] font-display font-black uppercase tracking-[0.2em]">Signal Processing</span>
             </div>
             <div className="flex flex-col gap-3">
               <div className={`p-4 rounded-xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-950/5 shadow-sm'}`}>
                 <ControlGroup label="Signal Cutoff Limiter" settingKey="audioCutoffEnabled" darkMode={config.darkMode}>
                    <Toggle active={config.audioCutoffEnabled} onClick={() => setConfig(p => ({...p, audioCutoffEnabled: !p.audioCutoffEnabled}))} />
                 </ControlGroup>
               </div>
               <div className={`p-4 rounded-xl border ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-950/5 shadow-sm'}`}>
                 <ControlGroup label="Polyphonic Harmony" settingKey="audioCompoundMode" darkMode={config.darkMode}>
                    <Toggle active={config.audioCompoundMode} onClick={() => setConfig(p => ({...p, audioCompoundMode: !p.audioCompoundMode}))} />
                 </ControlGroup>
               </div>
             </div>
           </div>
        </div>
      </div>

      {/* Cloud Archive */}
      <div className="flex flex-col gap-8">
        <div className="flex items-center gap-3 mb-8 px-1">
          <div className="w-8 h-8 rounded-xl bg-neon-cyan/20 flex items-center justify-center shadow-lg border border-cyan-500/20">
            <Layers className="w-4 h-4 text-cyan-500" />
          </div>
          <h3 className="text-[10px] font-display font-black uppercase tracking-[0.2em] opacity-40">Factory Presets</h3>
        </div>
        <PresetLibrary config={config} setConfig={setConfig} cloudSaves={cloudSaves} />
      </div>
    </div>
  );
};
